import { QueryClient } from '@tanstack/react-query';
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { db } from '../db';

const CACHE_KEY = 'react-query-cache';

// Custom persister using Dexie.
//
// `userPreferences` is keyed by an auto-increment `id`; `key` is only a
// secondary index. A bare `put()` without an `id` therefore *inserts* a new row
// on every persist instead of replacing the previous one, and a `where('key')`
// lookup returns matches ordered by primary key — i.e. the oldest row. That
// combination made the app restore the very first cache it ever wrote and grow
// the table without bound. We resolve the existing primary key explicitly and
// prune any duplicate rows left behind.
const dexiePersister: Persister = {
  persistClient: async (client: PersistedClient) => {
    const [keep, ...duplicates] = (await db.userPreferences
      .where('key')
      .equals(CACHE_KEY)
      .primaryKeys()) as number[];

    if (duplicates.length > 0) {
      await db.userPreferences.bulkDelete(duplicates);
    }

    await db.userPreferences.put({
      ...(keep !== undefined && { id: keep }),
      key: CACHE_KEY,
      value: JSON.stringify(client),
      updatedAt: Date.now(),
    });
  },
  restoreClient: async () => {
    const rows = await db.userPreferences.where('key').equals(CACHE_KEY).toArray();

    if (rows.length === 0) {
      return undefined;
    }

    const newest = rows.reduce((a, b) => (b.updatedAt > a.updatedAt ? b : a));

    try {
      return JSON.parse(newest.value) as PersistedClient;
    } catch {
      return undefined;
    }
  },
  removeClient: async () => {
    await db.userPreferences.where('key').equals(CACHE_KEY).delete();
  },
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale-while-revalidate: cached data paints instantly, but every mount
      // (i.e. every client-side navigation), window focus and reconnect kicks
      // off a background refetch. A non-zero default meant navigating back to a
      // screen inside the stale window showed cached data with no revalidation,
      // so the only way to see fresh data was a full page reload. Queries over
      // genuinely static reference data opt out with their own `staleTime`.
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // Keep unobserved data around so navigation renders from cache while the
      // refetch is in flight, and so the persisted cache is useful offline.
      gcTime: 1000 * 60 * 60 * 24 * 7, // 7 days
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error instanceof Error && error.message.includes('API Error: 4')) {
          return false;
        }
        return failureCount < 3;
      },
      // Always attempt the request even when the browser reports itself offline
      // (`navigator.onLine` is unreliable); only retries are paused.
      networkMode: 'offlineFirst',
    },
    mutations: {
      networkMode: 'offlineFirst',
      retry: false, // Don't retry mutations automatically
    },
  },
});

// Persist query client
export const persister = dexiePersister;
